import { FC, useMemo } from 'react';
import { PieChart, Pie, Cell, Label, LabelProps } from 'recharts';
import { GridLegacy as Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { Card, CardContent, CardFooter } from './common/card';
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
} from './common/chart-config';
import { CSVDownloader } from '../fileDownload/csv-downloader';
import { IChartOptions, isChartOptions } from './common/chart-options';
import { IChartProps } from './common/types';
import { useUserParams } from '../../context/user-params-context';
import { formatLabel, formatMinutesToHHMM } from './common/format-helpers';
import { ChartInfoTooltip } from './common/card-info-tooltip';
import { useChartData } from '../../hooks/useChartData';
import { useReleaseManagement, ReleaseFlag } from '@/utility/feature-management';
import { resolveChartTitle } from './common/resolve-chart-title';
import { usePreferences } from '../../utility/usePreferences';
import { capitalise } from '../../utility/utils';

interface DataItem {
  name?: string;
  value: number;
  fill?: string;
}

interface IDonutChartOptions extends IChartOptions {
  displayMode?: 'name' | 'ratio' | 'figure' | 'sum' | 'hh:mm' | 'ratio_sum';
  baseMetric?: string;
  labelScale?: number;
  fill?: string;
  dataKey?: string;
}

interface PieLabelProps extends LabelProps {
  viewBox: { cx: number; cy: number };
}

export const DonutChart: FC<IChartProps> = ({ uuid }) => {
  const { t } = useTranslation();
  const { userParams, parameterTypes } = useUserParams();
  const { distancePerHour, distanceUnitText, distanceUnitSingular } = usePreferences();

  const { data, isLoading } = useChartData<DataItem[], IDonutChartOptions>(
    uuid,
    parameterTypes,
    userParams,
    { validateOptions: isChartOptions }
  );

  const releaseRefactorChartTitle = useReleaseManagement(
    ReleaseFlag.releaseRefactorChartTitle
  );

  const chartOptions = data?.options;
  const chartData = data?.data || [];
  const chartName = data?.name;
  const chartTranslationKey = data?.translationKey ?? undefined;

  const createChartConfig = (): ChartConfig => {
    if (!chartOptions) return {};
    const config: ChartConfig = {};
    Object.entries(chartOptions || {}).forEach(([key, pieOptions]: any) => {
      config[key] = {
        label: key,
        color: (pieOptions as any).fill || `var(--color-${key})`,
      };
    });
    return config;
  };

  const gridProps = {
    sm: chartOptions?.showLegend ? 9 : 6,
    md: chartOptions?.showLegend ? 7 : 4,
    lg: chartOptions?.showLegend ? 4 : 3,
  };

  const getLabel = ({ viewBox }: PieLabelProps) => {
    let text = [t('general.loading')];

    if (chartOptions?.displayMode === 'name') {
      text = data?.name?.split(' ') || [];
    }

    if (chartOptions?.displayMode === 'ratio') {
      text = [chartData.map((i) => i.value).join(' / ')];
    }

    if (chartOptions?.displayMode === 'figure') {
      text = [
        `${chartData.find(({ name }) => name === chartOptions.baseMetric)?.value ||
        0
        }`,
      ];
    }

    if (chartOptions?.displayMode === 'sum') {
      text = [
        `${chartData.reduce(
          (acc: number, curr: DataItem) => acc + curr.value,
          0
        )}`,
      ];
    }

    if (chartOptions?.displayMode === 'hh:mm') {
      text = [
        formatMinutesToHHMM(
          `${chartData.reduce(
            (acc: number, curr: DataItem) => acc + curr.value,
            0
          )}`
        ),
      ];
    }

    if (chartOptions?.displayMode === 'ratio_sum') {
      const first = chartData[0]?.value || 0;
      const sum = chartData.reduce(
        (acc: number, curr: DataItem) => acc + curr.value,
        0
      );
      text = [`${first} / ${sum}`];
    }

    const LABEL_SCALE = chartOptions?.labelScale || 24;

    return (
      <text
        textAnchor="middle"
        fontSize={LABEL_SCALE}
        fontWeight="bold"
        fill="black"
        dy="0"
      >
        {text.map((word, i) => (
          <tspan
            key={word}
            x={viewBox.cx}
            y={
              viewBox.cy +
              LABEL_SCALE / 4 -
              (text.length - 1) * (LABEL_SCALE / 2)
            }
            dy={`${i}em`}
          >
            {word}
          </tspan>
        ))}
      </text>
    );
  };

  const everyValueIsFalsy = chartData.every((i) => !i.value);
  const decoratedData = everyValueIsFalsy
    ? [{ fill: '#ccc', value: 1 }]
    : chartData;

  const computedTitle = useMemo(() => {
    return resolveChartTitle({
      t,
      name: chartName ?? undefined,
      translationKey: chartTranslationKey,
      featureEnabled: releaseRefactorChartTitle,
      defaultText: chartName ?? undefined, // fallback to original name
      i18nParams: {
        distancePerHour,
        distanceUnit: capitalise(distanceUnitText),
        distanceUnitSingular: capitalise(distanceUnitSingular),
        timeFormat: 'hh:mm',
      },
    });
  }, [
    t,
    chartName,
    chartTranslationKey,
    releaseRefactorChartTitle,
    distancePerHour,
    distanceUnitText,
    distanceUnitSingular,
  ]);

  return (
    <Grid item {...gridProps}>
      <Card
        style={{
          animation: isLoading ? 'pulse 2s linear infinite' : undefined,
        }}
      >
        {chartOptions?.description && (
          <ChartInfoTooltip description={chartOptions.description} />
        )}
        <CardContent>
          <ChartContainer config={createChartConfig()}>
            <PieChart className="donut">
              <Pie
                activeIndex={1}
                data={decoratedData}
                innerRadius={90}
                outerRadius={120}
                dataKey={chartOptions?.dataKey || 'value'}
                startAngle={90}
                endAngle={-270}
              >
                {decoratedData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
                {/* @ts-expect-error-next-line */}
                <Label content={getLabel} />
              </Pie>
              {!everyValueIsFalsy && (
                <ChartTooltip
                  content={
                    <ChartTooltipContent
                      heading={chartOptions?.tooltip?.heading}
                      nameKey={chartOptions?.tooltip?.nameKey}
                      labelFormatter={(value) =>
                        formatLabel(userParams, {
                          ...chartOptions?.tooltip?.label,
                          value,
                        })
                      }
                    />
                  }
                />
              )}
              {chartOptions?.showLegend && !everyValueIsFalsy && (
                <ChartLegend
                  verticalAlign="middle"
                  align="right"
                  layout="vertical"
                  content={({ payload }) => (
                    <ul
                      style={{
                        padding: 0,
                        margin: 0,
                        color: 'black',
                        fontSize: 14,
                        textAlign: 'left',
                        listStyleType: 'none',
                      }}
                    >
                      {payload?.map((item, i) => (
                        <li key={`item-${i}`} style={{ margin: '8px 0' }}>
                          <span
                            style={{
                              display: 'inline-block',
                              height: 12,
                              width: 12,
                              borderRadius: 2,
                              backgroundColor: item.color,
                              marginRight: 8,
                              verticalAlign: 'middle',
                            }}
                          />
                          <span>{item.value}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                />
              )}
            </PieChart>
          </ChartContainer>
        </CardContent>
        <CardFooter>
          <Typography variant="subtitle1">
            {isLoading ? t('general.loading') : computedTitle}
          </Typography>
          {chartOptions?.enableCSVDownload && (
            <CSVDownloader data={chartData} filename={chartName || ''} />
          )}
        </CardFooter>
      </Card>
    </Grid>
  );
};

export default DonutChart;
